import type { SdkUser } from '@dynamic-labs/sdk-api-core';
import { JwtVerifiedCredentialFormatEnum } from '@dynamic-labs/sdk-api-core';

import { getCore } from '../../../../client/core/getCore';
import type { DynamicClient } from '../../../../client/types';
import type { UnverifiedWalletAccount } from '../../unverifiedWalletAccounts';
import { convertUnverifiedWalletAccountToWalletAccount } from '../../utils/convertUnverifiedWalletAccountToWalletAccount';
import { convertVerifiedCredentialToWalletAccount } from '../../utils/convertVerifiedCredentialToWalletAccount';
import type { WalletAccount } from '../../walletAccount';

type GetWalletAccountsFromStateParams = {
  unverifiedWalletAccounts: UnverifiedWalletAccount[];
  user: SdkUser | null;
};

/** @notInstrumented */
export const getWalletAccountsFromState = (
  { unverifiedWalletAccounts, user }: GetWalletAccountsFromStateParams,
  client: DynamicClient
): WalletAccount[] => {
  const walletAccountsMap: Map<string, WalletAccount> = new Map();

  /**
   * Handle the unverified wallet accounts before the user verified credentials
   * so the later verified wallet accounts can override the unverified wallet accounts
   */
  unverifiedWalletAccounts.forEach((unverifiedWalletAccount) => {
    const walletAccount = convertUnverifiedWalletAccountToWalletAccount({
      unverifiedWalletAccount,
    });

    walletAccountsMap.set(walletAccount.id, walletAccount);
  });

  /**
   * Handle user verified credentials
   */
  const verifiedCredentials = user?.verifiedCredentials ?? [];

  verifiedCredentials
    .filter(
      (verified) =>
        verified.format === JwtVerifiedCredentialFormatEnum.Blockchain
    )
    .forEach((verifiedWalletAccount) => {
      try {
        const walletAccount = convertVerifiedCredentialToWalletAccount(
          {
            verifiedCredential: verifiedWalletAccount,
          },
          client
        );

        walletAccountsMap.set(walletAccount.id, walletAccount);
      } catch (error) {
        /**
         * A verified credential can be missing the data required to resolve its
         * wallet provider (e.g. a legacy credential without a walletName). Skip
         * it instead of throwing so a single unconvertible credential does not
         * abort the whole mapping — which, during client initialization, would
         * otherwise fail init and leave the app stuck loading. The wallet stays
         * absent until the user reconnects it.
         */
        getCore(client).logger.debug(
          `Skipping verified credential ${verifiedWalletAccount.id}: unable to convert to wallet account`,
          error
        );
      }
    });

  return Array.from(walletAccountsMap.values());
};
